import React, { useState, useEffect } from 'react';
import { FiMail, FiCopy, FiClock, FiCheck, FiShield, FiMoreVertical } from 'react-icons/fi';
import { useEmailInit } from '../hooks/useEmailInit';
import { useTimeRemaining } from '../hooks/useTimeRemaining';
import '../styles/EmailPanel.css';

const EmailPanel = () => {
    const [copied, setCopied] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { tempEmail, loading, error, remainingTimeInSeconds, initializeEmail, reinitializeEmail, extendEmailTime } = useEmailInit();
    const { hours, minutes, seconds, isExpired } = useTimeRemaining(remainingTimeInSeconds);

    useEffect(() => {
        initializeEmail();
    }, [initializeEmail]);

    useEffect(() => {
        if (isExpired) {
            reinitializeEmail();
        }
    }, [isExpired, reinitializeEmail]);

    const handleCopy = () => {
        navigator.clipboard.writeText(tempEmail);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
        setMenuOpen(!menuOpen);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const closeMenu = () => setMenuOpen(false);
        if (menuOpen) {
            document.addEventListener('click', closeMenu);
            return () => document.removeEventListener('click', closeMenu);
        }
    }, [menuOpen]);

    return (
        <div className="email-panel">
            {error ? (
                <div className="error-message">
                    <FiMail /> {error}
                </div>
            ) : loading ? (
                <div className="loading-state">
                    <div className="shimmer-wrapper">
                        <div className="shimmer-line" />
                        <div className="shimmer-line shimmer-line--short" />
                    </div>
                </div>
            ) : (
                <>
                    <div className="email-display">
                        <div className="email-address">
                            {tempEmail}
                        </div>
                        <div className="button-group">
                            <div className={`copy-button-wrapper ${copied ? 'copied' : ''}`}>
                                <button 
                                    className="copy-button"
                                    onClick={handleCopy}
                                    aria-label="Copy email address"
                                >
                                    <FiCopy className="copy-icon" />
                                    <FiCheck className="check-icon" />
                                    <span className="button-text">Copy</span>
                                </button>
                            </div>
                            <div className="menu-button-wrapper">
                                <button 
                                    className="menu-button"
                                    onClick={handleMenuClick}
                                    aria-label="Menu options"
                                >
                                    <FiMoreVertical />
                                    <span className="button-text">Menu</span>
                                </button>
                                {menuOpen && (
                                    <div className="menu-dropdown">
                                        <button onClick={reinitializeEmail}>
                                            Get Another Email
                                        </button>
                                        <button onClick={extendEmailTime}>
                                            Extend Time (+1 hour)
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="timer-container">
                        <div className="timer-header">
                            <FiClock />
                            <span>Email expires in:</span>
                        </div>
                        <div className="countdown">
                            <div className="time-block">
                                <span className="time-value">{hours}</span>
                                <span className="time-label">Hrs</span>
                            </div>
                            <div className="time-separator">:</div>
                            <div className="time-block">
                                <span className="time-value">{minutes}</span>
                                <span className="time-label">Min</span>
                            </div>
                            <div className="time-separator">:</div>
                            <div className="time-block">
                                <span className="time-value">{seconds}</span>
                                <span className="time-label">Sec</span>
                            </div>
                        </div>
                    </div>

                    <div className="bottom-text">
                        <div className="info-message">
                            <FiClock className="info-icon" />
                            <span>Your temporary email will expire when the countdown reaches zero</span>
                        </div>
                        <div className="disclaimer">
                            <FiShield className="check-icon" />
                            <span>All data is automatically deleted after expiration for privacy</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default EmailPanel; 